import { validateHazmat } from "./hazmat.js";
import { validateCustoms, type CustomsInfo } from "./crossborder.js";
import type { Money } from "./money.js";
import type { ShipmentStatus } from "./shipment-status.js";

/**
 * Carriers with adapters. `eagleload` (EagleLoad) and `wwex` (Worldwide Express Speedship) are aggregators: their
 * options name the real carrier in `carrierName`.
 * `mock` is the test/demo adapter.
 */
export const CARRIER_CODES = ["xpo", "odfl", "estes", "saia", "rl", "abf", "fedex_freight", "tforce", "amfast", "eagleload", "wwex", "mock"] as const;
export type CarrierCode = (typeof CARRIER_CODES)[number];

/** NMFC freight classes. */
export const FREIGHT_CLASSES = [
  "50", "55", "60", "65", "70", "77.5", "85", "92.5", "100", "110",
  "125", "150", "175", "200", "250", "300", "400", "500",
] as const;
export type FreightClass = (typeof FREIGHT_CLASSES)[number];

/**
 * Normalized accessorial code. The catalog and per-carrier code maps live in the accessorial
 * module (task 5); this is the shared identifier, kept as a branded string so the catalog can grow.
 */
export type AccessorialCode = string & { readonly __accessorial?: never };

export interface Accessorial {
  code: AccessorialCode;
  /** Where the service applies. */
  stop: "pickup" | "delivery" | "shipment";
}

export type CountryCode = "US" | "CA" | "MX";

export interface Address {
  /** Street. "" on a ZIP-only quote (carriers rate on ZIP, city and state); always filled in before booking. */
  line1: string;
  line2?: string;
  city: string;
  /** Two-letter state/province code. */
  region: string;
  postalCode: string;
  country: CountryCode;
}

export type LocationType =
  | "business_dock"
  | "business_no_dock"
  | "residential"
  | "limited_access"
  | "trade_show"
  | "construction";

export interface Contact {
  name: string;
  phone?: string;
  email?: string;
}

export interface Location {
  address: Address;
  companyName?: string;
  type: LocationType;
  contact?: Contact;
  /** Local dock hours, "HH:MM" 24h. */
  hours?: { open: string; close: string };
  instructions?: string;
}

export type PackagingType = "pallet" | "skid" | "crate" | "box" | "drum" | "bundle" | "roll" | "tote" | "other";

export interface Dimensions {
  /** Inches. */
  lengthIn: number;
  widthIn: number;
  heightIn: number;
}

export interface Hazmat {
  unNumber: string;
  hazardClass: string;
  packingGroup?: "I" | "II" | "III";
  properShippingName: string;
  /** 24-hour emergency response phone (49 CFR 172.604). */
  emergencyPhone: string;
  /** Person or registered provider (e.g. CHEMTREC contract) answering that phone. */
  emergencyContactName?: string;
}

export interface HandlingUnit {
  packaging: PackagingType;
  quantity: number;
  /** Total weight of all `quantity` units, pounds. */
  weightLb: number;
  /** Per-unit dimensions. */
  dims: Dimensions;
  freightClass?: FreightClass;
  /**
   * NMFC density class a class-less unit was rated at (set by the server when the quote is stored,
   * never accepted from clients). Ignored when `freightClass` is declared. Printed "density-rated".
   */
  ratedFreightClass?: FreightClass;
  nmfc?: string;
  description: string;
  stackable: boolean;
  hazmat?: Hazmat;
  /** Pieces inside the handling units (cartons on the pallets), for the BOL. */
  pieces?: number;
}

export interface QuoteRequest {
  customerId?: string;
  origin: Location;
  destination: Location;
  /** ISO date "YYYY-MM-DD". */
  pickupDate: string;
  units: HandlingUnit[];
  accessorials: Accessorial[];
  /** Declared value for excess liability, if any. */
  declaredValue?: Money;
  /** Restrict to these carriers; empty/undefined = all enabled carriers. */
  carriers?: CarrierCode[];
  /** US <-> Canada customs broker and PARS/PAPS data; only valid on cross-border lanes. */
  customs?: CustomsInfo;
}

export type ChargeKind = "linehaul" | "fuel" | "accessorial" | "discount" | "guarantee" | "tax" | "other";

export interface ChargeLine {
  kind: ChargeKind;
  /** Accessorial code when kind = "accessorial". */
  code?: AccessorialCode;
  description: string;
  amount: Money;
}

export type ServiceLevel = "standard" | "guaranteed" | "guaranteed_am" | "expedited";

export interface RateOption {
  id: string;
  carrier: CarrierCode;
  carrierName: string;
  /** Carrier quote reference, required by some carriers at booking. */
  carrierQuoteId?: string;
  service: ServiceLevel;
  serviceName: string;
  transitDays: number;
  /** ISO date. */
  estimatedDelivery?: string;
  guaranteed: boolean;
  /** Carrier charges (our cost). */
  charges: ChargeLine[];
  cost: Money;
  /** Customer price after markup. Undefined until the markup engine runs. */
  price?: Money;
  appliedMarkupRuleIds?: string[];
  /** ISO timestamp. */
  expiresAt: string;
  /**
   * Carrier response for this option as received, stored for audit and disputes. Internal only:
   * never returned to customers, never contains our credentials.
   */
  raw?: unknown;
}

export interface CarrierRateError {
  carrier: CarrierCode;
  code: string;
  message: string;
}

export interface Quote {
  id: string;
  orgId: string;
  request: QuoteRequest;
  options: RateOption[];
  errors: CarrierRateError[];
  /** ISO timestamps. */
  createdAt: string;
  expiresAt: string;
}

export interface ShipmentReferences {
  bol?: string;
  pro?: string;
  po?: string[];
  pickupConfirmation?: string;
  customer?: string;
  /** The shipment's id in the carrier's or aggregator's own system (EagleLoad shipment id), for tracking and webhooks. */
  carrierShipmentId?: string;
}

export interface Shipment {
  id: string;
  orgId: string;
  quoteId: string;
  rateOptionId: string;
  carrier: CarrierCode;
  status: ShipmentStatus;
  origin: Location;
  destination: Location;
  units: HandlingUnit[];
  accessorials: Accessorial[];
  references: ShipmentReferences;
  cost: Money;
  price: Money;
  pickupDate: string;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------------------------
// Validation (pure, dependency-free). API layers wrap this; returns all issues, not just the first.
// ---------------------------------------------------------------------------------------------

export interface ValidationIssue {
  path: string;
  message: string;
}

export const LIMITS = {
  maxUnits: 50,
  maxUnitQuantity: 100,
  /** Pounds per handling-unit line. LTL tops out well below this. */
  maxLineWeightLb: 45_000,
  maxTotalWeightLb: 45_000,
  /** Inches; 53' trailer. */
  maxLengthIn: 636,
  maxWidthIn: 102,
  maxHeightIn: 110,
  maxPickupDaysAhead: 60,
  maxTextLength: 200,
} as const;

const POSTAL: Record<CountryCode, RegExp> = {
  US: /^\d{5}(-\d{4})?$/,
  CA: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
  MX: /^\d{5}$/,
};

function isPositiveFinite(n: unknown, maxValue: number): boolean {
  return typeof n === "number" && Number.isFinite(n) && n > 0 && n <= maxValue;
}

function text(issues: ValidationIssue[], path: string, v: unknown, required: boolean): void {
  if (v === undefined || v === null || v === "") {
    if (required) issues.push({ path, message: "required" });
    return;
  }
  if (typeof v !== "string") issues.push({ path, message: "must be a string" });
  else if (v.length > LIMITS.maxTextLength) issues.push({ path, message: `max ${LIMITS.maxTextLength} chars` });
}

/**
 * `streetRequired: false` is the quote-time check: carriers rate on ZIP, city and state, so a quote may leave the
 * street blank (""); booking requires the full address.
 */
export function validateAddress(a: Address, path = "address", opts: { streetRequired?: boolean } = {}): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (a === null || typeof a !== "object") return [{ path, message: "required" }];
  text(issues, `${path}.line1`, a.line1, opts.streetRequired ?? true);
  text(issues, `${path}.line2`, a.line2, false);
  text(issues, `${path}.city`, a.city, true);
  if (typeof a.region !== "string" || !/^[A-Za-z]{2}$/.test(a.region))
    issues.push({ path: `${path}.region`, message: "2-letter code" });
  // Own-key lookup: a prototype key ("constructor", "toString") must not resolve to a function.
  const re = typeof a.country === "string" && Object.hasOwn(POSTAL, a.country) ? POSTAL[a.country] : undefined;
  if (!re) issues.push({ path: `${path}.country`, message: "unsupported country" });
  else if (typeof a.postalCode !== "string" || !re.test(a.postalCode))
    issues.push({ path: `${path}.postalCode`, message: "invalid postal code" });
  return issues;
}

export function validateHandlingUnit(u: HandlingUnit, path = "unit"): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (u === null || typeof u !== "object") return [{ path, message: "required" }];
  if (!Number.isInteger(u.quantity) || u.quantity < 1 || u.quantity > LIMITS.maxUnitQuantity)
    issues.push({ path: `${path}.quantity`, message: `integer 1-${LIMITS.maxUnitQuantity}` });
  if (!isPositiveFinite(u.weightLb, LIMITS.maxLineWeightLb))
    issues.push({ path: `${path}.weightLb`, message: `0-${LIMITS.maxLineWeightLb} lb` });
  const d = u.dims !== null && typeof u.dims === "object" ? u.dims : ({} as Dimensions);
  if (!isPositiveFinite(d.lengthIn, LIMITS.maxLengthIn)) issues.push({ path: `${path}.dims.lengthIn`, message: "invalid" });
  if (!isPositiveFinite(d.widthIn, LIMITS.maxWidthIn)) issues.push({ path: `${path}.dims.widthIn`, message: "invalid" });
  if (!isPositiveFinite(d.heightIn, LIMITS.maxHeightIn)) issues.push({ path: `${path}.dims.heightIn`, message: "invalid" });
  if (u.freightClass !== undefined && !(FREIGHT_CLASSES as readonly string[]).includes(u.freightClass))
    issues.push({ path: `${path}.freightClass`, message: "not an NMFC class" });
  if (u.ratedFreightClass !== undefined && !(FREIGHT_CLASSES as readonly string[]).includes(u.ratedFreightClass))
    issues.push({ path: `${path}.ratedFreightClass`, message: "not an NMFC class" });
  if (u.nmfc !== undefined && (typeof u.nmfc !== "string" || !/^\d{1,6}(-\d{1,2})?$/.test(u.nmfc)))
    issues.push({ path: `${path}.nmfc`, message: "invalid NMFC item" });
  text(issues, `${path}.description`, u.description, true);
  if (u.hazmat) issues.push(...validateHazmat(u.hazmat, `${path}.hazmat`));
  return issues;
}

/** Parses "YYYY-MM-DD" as a UTC date; returns null when malformed or not a real calendar date. */
export function parseIsoDate(s: string): Date | null {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T00:00:00Z`);
  return Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== s ? null : d;
}

export function validateQuoteRequest(req: QuoteRequest, now: Date = new Date()): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  // ZIP-first quoting: the street (and company) are optional until booking.
  issues.push(...validateAddress(req.origin?.address ?? ({} as Address), "origin.address", { streetRequired: false }));
  issues.push(...validateAddress(req.destination?.address ?? ({} as Address), "destination.address", { streetRequired: false }));

  const pickup = parseIsoDate(req.pickupDate ?? "");
  if (!pickup) {
    issues.push({ path: "pickupDate", message: "YYYY-MM-DD" });
  } else {
    // Compare in whole UTC days; allow "today" and one day of timezone slack.
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const days = Math.round((pickup.getTime() - today) / 86_400_000);
    if (days < -1) issues.push({ path: "pickupDate", message: "in the past" });
    if (days > LIMITS.maxPickupDaysAhead) issues.push({ path: "pickupDate", message: `max ${LIMITS.maxPickupDaysAhead} days ahead` });
  }

  if (!Array.isArray(req.units) || req.units.length === 0) {
    issues.push({ path: "units", message: "at least one handling unit" });
  } else if (req.units.length > LIMITS.maxUnits) {
    issues.push({ path: "units", message: `max ${LIMITS.maxUnits} lines` });
  } else {
    req.units.forEach((u, i) => issues.push(...validateHandlingUnit(u, `units[${i}]`)));
    const total = req.units.reduce((t, u) => t + (u && Number.isFinite(u.weightLb) && u.weightLb > 0 ? u.weightLb : 0), 0);
    if (total > LIMITS.maxTotalWeightLb) issues.push({ path: "units", message: `total weight over ${LIMITS.maxTotalWeightLb} lb` });
  }

  if (!Array.isArray(req.accessorials)) issues.push({ path: "accessorials", message: "must be an array" });
  else {
    const seen = new Set<string>();
    req.accessorials.forEach((a, i) => {
      if (a === null || typeof a !== "object") {
        issues.push({ path: `accessorials[${i}]`, message: "invalid accessorial" });
        return;
      }
      if (a.stop !== "pickup" && a.stop !== "delivery" && a.stop !== "shipment")
        issues.push({ path: `accessorials[${i}].stop`, message: "pickup, delivery or shipment" });
      if (typeof a.code !== "string" || !/^[a-z][a-z0-9_]{1,40}$/.test(a.code))
        issues.push({ path: `accessorials[${i}].code`, message: "invalid code" });
      const key = `${a.stop}:${a.code}`;
      if (seen.has(key)) issues.push({ path: `accessorials[${i}]`, message: "duplicate" });
      seen.add(key);
    });
  }

  if (req.declaredValue && (!Number.isSafeInteger(req.declaredValue.cents) || req.declaredValue.cents < 0))
    issues.push({ path: "declaredValue", message: "non-negative cents" });

  if (req.carriers !== undefined && req.carriers !== null && !Array.isArray(req.carriers))
    issues.push({ path: "carriers", message: "must be an array" });
  else if (req.carriers?.some((c) => !(CARRIER_CODES as readonly string[]).includes(c)))
    issues.push({ path: "carriers", message: "unknown carrier" });

  if (req.customs && req.origin?.address && req.destination?.address)
    issues.push(...validateCustoms(req.customs, { origin: req.origin.address, destination: req.destination.address }));

  return issues;
}

/** Total shipment weight in pounds. */
export function totalWeightLb(units: readonly HandlingUnit[]): number {
  return units.reduce((t, u) => t + u.weightLb, 0);
}

/** True when any unit or accessorial marks the shipment hazardous. */
export function isHazmat(req: Pick<QuoteRequest, "units" | "accessorials">): boolean {
  return req.units.some((u) => u.hazmat !== undefined) || req.accessorials.some((a) => a.code === "hazmat");
}

/** A rate option is bookable until its expiry. */
export function isRateExpired(opt: Pick<RateOption, "expiresAt">, now: Date = new Date()): boolean {
  const t = Date.parse(opt.expiresAt);
  return Number.isNaN(t) || t <= now.getTime();
}
